import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { NAV_CATEGORIES, CATEGORY_META } from "@/i18n/translations";
import { BRAND_SIGNATURE, signatureTooltip } from "@/lib/brand";

function Logo({ lang }) {
  return (
    <span className="flex items-center gap-2.5" title={`${BRAND_SIGNATURE}\n${signatureTooltip(lang)}`} data-testid="brand-logo">
      <img src="/logo-rsti-96.png" alt="RSTI" className="h-9 w-9" />
      <span className="flex items-baseline gap-2">
        <span className="font-serif text-2xl font-semibold tracking-tight text-anthracite">RSTI</span>
        <span className="hidden text-[10px] uppercase tracking-[0.3em] text-champagne sm:inline">Global</span>
      </span>
    </span>
  );
}

export default function Header() {
  const { lang, changeLang, t } = useLang();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const routeFor = (c) => (c === "opportunities" ? "/opportunites" : c === "projects" ? "/projets" : c === "investment" ? "/investissement" : c === "partners" ? "/partenaires" : c === "sourcing" ? "/sourcing" : c === "markets" ? "/marches" : `/category/${c}`);
  const links = [
    ...NAV_CATEGORIES.map((c) => ({ to: routeFor(c), label: CATEGORY_META[c][lang] })),
    { to: "/experts", label: "Expertise" },
    { to: "/market-test", label: "Market Test" },
  ];

  return (
    <header className="sticky top-0 z-50 border-b border-tertiary bg-ivory/95 backdrop-blur-sm">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 lg:px-10">
        <Link to="/" data-testid="logo-link" className="mr-6 shrink-0" onClick={() => setOpen(false)}>
          <Logo lang={lang} />
        </Link>

        <nav className="hidden items-center gap-5 whitespace-nowrap lg:flex">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.split("/").pop()}`}
              className={({ isActive }) =>
                `text-sm transition-colors duration-300 hover:text-champagne ${
                  isActive ? "text-champagne" : "text-anthracite/80"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <NavLink
            to="/connect"
            data-testid="nav-connect"
            className="text-sm font-medium text-anthracite transition-colors duration-300 hover:text-champagne"
          >
            {t("nav_connect")}
          </NavLink>
        </nav>

        <div className="flex items-center gap-4">
          <div className="ml-4 hidden items-center gap-1 text-xs sm:flex" data-testid="lang-switcher">
            {["fr", "en"].map((l) => (
              <button
                key={l}
                data-testid={`lang-${l}`}
                onClick={() => changeLang(l)}
                className={`px-1.5 py-0.5 uppercase transition-colors duration-300 ${
                  lang === l ? "font-semibold text-anthracite" : "text-anthracite/40 hover:text-anthracite"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <button
            onClick={() => navigate("/connect")}
            data-testid="header-cta"
            className="hidden whitespace-nowrap bg-anthracite px-5 py-2.5 text-sm font-medium text-ivory transition-colors duration-300 hover:bg-black md:inline-block"
          >
            {t("cta_connect")}
          </button>
          {user ? (
            <button
              onClick={() => navigate(user.role === "member" ? "/account" : "/admin")}
              data-testid="header-account"
              className="hidden text-sm text-anthracite transition-colors duration-300 hover:text-champagne md:inline-block"
            >
              {user.role === "member" ? t("member_space") : "Admin"}
            </button>
          ) : (
            <button
              onClick={() => navigate("/login")}
              data-testid="header-login"
              className="hidden text-sm text-anthracite/80 transition-colors duration-300 hover:text-champagne md:inline-block"
            >
              {t("member_login")}
            </button>
          )}
          <button
            className="lg:hidden"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-tertiary bg-ivory lg:hidden" data-testid="mobile-menu">
          <nav className="flex flex-col px-6 py-4">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="border-b border-tertiary py-3 text-sm text-anthracite/80"
              >
                {l.label}
              </Link>
            ))}
            <Link to="/connect" onClick={() => setOpen(false)} className="py-3 text-sm font-medium text-anthracite">
              {t("nav_connect")}
            </Link>
            {user ? (
              <Link to={user.role === "member" ? "/account" : "/admin"} onClick={() => setOpen(false)} className="border-t border-tertiary py-3 text-sm text-anthracite/80">
                {user.role === "member" ? t("member_space") : "Admin"}
              </Link>
            ) : (
              <div className="flex gap-4 border-t border-tertiary py-3">
                <Link to="/login" onClick={() => setOpen(false)} className="text-sm text-anthracite/80">{t("member_login")}</Link>
                <Link to="/signup" onClick={() => setOpen(false)} className="text-sm font-medium text-champagne">{t("member_signup")}</Link>
              </div>
            )}
            <div className="flex gap-3 py-3">
              {["fr", "en"].map((l) => (
                <button
                  key={l}
                  onClick={() => { changeLang(l); setOpen(false); }}
                  className={`text-xs uppercase ${lang === l ? "font-semibold text-anthracite" : "text-anthracite/40"}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
